import { globalModalsHiddenAtom } from '@/app-shell/app-root/globalModalAtoms';
import { isOnPrem } from '@/utils/buildProfile';
import { useAtomValue } from 'jotai';
import { memo } from 'react';

import CloudOnlyGlobalModals from './global-modals-cloud-only';
import { E2EExemptGlobalModals, UniversalGlobalModals } from './global-modals-universal';

/**
 * The one app-root host for global modals, on every build.
 *
 * The default is universal and the exception is explicit. Cloud-only surfaces
 * (trials, paywalls, Kinde migration, onboarding, the billing-derived state
 * they orchestrate) live behind the `!isOnPrem` branch; everything else mounts
 * everywhere.
 *
 * That inversion is the point. The previous shape was an allowlist — a
 * `GlobalModalsWrapper` mounted only off-prem, plus a small `SharedGlobalModals`
 * an author had to remember to opt into — so a new modal was cloud-only by
 * default and its absence on-prem was silent: the trigger set its atom and
 * nothing rendered. Four fixes of exactly that shape shipped before the pattern
 * was named (MOD-1431 twice, PR #16454, then MOD-1632, which found on-prem
 * running with no idle logout and none of the desktop recording-recovery
 * modals). Now the failure mode is a cloud surface appearing on an appliance,
 * which QA can see.
 *
 * `isOnPrem` is a build-time const, so this reads as a static branch — but note
 * it does NOT currently strip the cloud tree from the on-prem bundle: rspack
 * does no scope hoisting across modules, which is why every SaaS marker still
 * sits in `KNOWN_LEAKS` (ARD-573, `scripts/assert-onprem-bundle-safety.mjs`).
 * The module boundary is kept so there is exactly one edge to cut when that is
 * fixed; it is not load-bearing today, and nothing here should claim otherwise.
 */
type GlobalModalsProps = {
  /** Running-timer signal from the store-coupled `_app` host; feeds the
   * universal tree's launch-queue state sync. */
  isTimerRunning?: boolean;
};

const GlobalModals = ({ isTimerRunning }: GlobalModalsProps) => {
  const globalModalsHidden = useAtomValue(globalModalsHiddenAtom);

  // The E2E suppression and the `globalModalsHidden` escape hatch previously
  // covered only the cloud tree, because on-prem mounted a different component
  // that never consulted them. Applying both here makes the two builds behave
  // the same way under test.
  if (globalModalsHidden) {
    return null;
  }
  // E2E suppression hides the whole global tree so announcement modals never
  // intercept test clicks — except modals flagged `isNotSuppressedInE2E`
  // (steps under test), which `E2EExemptGlobalModals` renders.
  if (
    typeof window !== 'undefined' &&
    window.__IS_E2E__ === true &&
    window.__SUPPRESS_GLOBAL_MODALS__ === true
  ) {
    return <E2EExemptGlobalModals />;
  }
  return (
    <>
      <UniversalGlobalModals isTimerRunning={isTimerRunning} />
      {!isOnPrem && <CloudOnlyGlobalModals />}
    </>
  );
};

// Memo barrier: this hosts ~50 always-mounted modal subtrees. Without it, every
// state change in _app's provider stack re-renders all of them on every commit.
export default memo(GlobalModals);
